import React, { useState, useEffect, useRef } from 'react';

import classNames from 'classnames';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import '../../../components/Crud.scss';
import * as _ from 'lodash';
import { ProgressBar } from 'primereact/progressbar';
import { v4 as uuidv4 } from 'uuid';
import {Checkbox} from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Paginator } from 'primereact/paginator';
import { PickList } from 'primereact/picklist';

import { AuthorizationService, MANAGE_CUSTOMERS } from '../../../service/AuthorizationService';
import { EntityService } from "../../../service/EntityService";
import { LanguageService } from "../../../service/LanguageService";
import { Fieldset } from 'primereact/fieldset';
import { InputMask } from 'primereact/inputmask';

function CustomerCrud() {

    const customersService = new EntityService('customers');
    const authorizationService = new AuthorizationService();
    const languageService = new LanguageService();
    const { client } = authorizationService.getCurrentUser();

    let emptyCustomer = {
        id: uuidv4(),
        client_id: client.id,
        name: '',
        type: 'PJ',
        registration_number: '',
        address: {},
    };

    const [customers, setCustomers] = useState([]);
    const [isCreation, setIsCreation] = useState(false);
    const [customerDialog, setCustomerDialog] = useState(false);
    const [deleteCustomerDialog, setDeleteCustomerDialog] = useState(false);
    const [customer, setCustomer] = useState(emptyCustomer);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [hasAccess, setHasAccess] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const toast = useRef(null);
    const dt = useRef(null);
    
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('name');
    const [sortOrder, setSortOrder] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [first, setFirst] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const updateList = async () => {
        const data = await customersService.list(first, rowsPerPage, search, sortField, sortOrder);
        setCustomers(data.items);
        setTotalRecords(data.totalRecords)
    }

    const onPageChange = async (pageEvent) => {
        setFirst(pageEvent.first);
        setRowsPerPage(pageEvent.rows);
        const data = await customersService.list(pageEvent.first, pageEvent.rows, search, sortField, sortOrder);
        setCustomers(data.items);
        setTotalRecords(data.totalRecords)
    }

    const doSearch = async (reset) => {
        const data = await customersService.list(first, rowsPerPage, reset ? null : globalFilter, sortField, sortOrder);
        setSearch(globalFilter);
        setCustomers(data.items);
        setTotalRecords(data.totalRecords)
    }

    const onSort = async (sortEvent) => {
        setSortField(sortEvent.sortField);
        setSortOrder(sortEvent.sortOrder);
        const data = await customersService.list(first, rowsPerPage, search, sortEvent.sortField, sortEvent.sortOrder);
        setCustomers(data.items);
        setTotalRecords(data.totalRecords)
    }

    useEffect(() => {
        setHasAccess(authorizationService.hasPermission(MANAGE_CUSTOMERS));

        if(authorizationService.hasPermission(MANAGE_CUSTOMERS)){
            async function fetchData() {
                await updateList();
                setLoading(false);
            }        
            fetchData();
        }
    }, []);

    const openNew = () => {
        emptyCustomer.id = uuidv4();
        setCustomer(emptyCustomer);
        setIsCreation(true);
        setSubmitted(false);
        setCustomerDialog(true);
    }

    const hideDialog = () => {
        if(!saving){
            setSubmitted(false);
            setCustomerDialog(false);
        }
    }

    const hideDeleteCustomerDialog = () => {
        setDeleteCustomerDialog(false);
    }

    const saveDisabled = () => {
        return (customer.name || "") === ""
            || (customer.type || "") === ""
            || (customer.registration_number || "") === "";
    }

    const saveCustomer = async () => {
        try {
            setSaving(true);
            setSubmitted(true);

            if (customer.name.trim()) {
                if (!isCreation) {
                    await customersService.update(customer);
                    toast.current.show({ severity: 'success', summary: languageService.getText('Successful'), detail: languageService.getText('Item_Updated'), life: 3000 });
                }
                else {
                    await customersService.create(customer);
                    toast.current.show({ severity: 'success', summary: languageService.getText('Successful'), detail: languageService.getText('Item_Created'), life: 3000 });
                }

                await updateList();
                setCustomerDialog(false);
                setCustomer(emptyCustomer);
                setSaving(false);
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: languageService.getText('Error'), detail: error.toString(), sticky: true });
            setSaving(false);
            console.log(error);
            throw error;
        }
    }

    const editCustomer = (customer) => {
        setCustomer({ ...customer });
        setIsCreation(false);
        setCustomerDialog(true);
    }

    const confirmDeleteCustomer = (customer) => {
        setCustomer(customer);
        setDeleteCustomerDialog(true);
    }

    const deleteCustomer = async () => {
        try {
            setSaving(true);
            await customersService.delete(customer);
            await updateList();
            setDeleteCustomerDialog(false);
            setCustomer(emptyCustomer);
            setSaving(false);
            toast.current.show({ severity: 'success', summary: languageService.getText('Successful'), detail: languageService.getText('Item_Deleted'), life: 3000 });    
        } catch (error) {
            console.log(error);
            throw error;
        }
    }

    const onInputChange = (e, name) => {
        const val = (e.target && e.target.value) || '';
        let _customer = { ...customer };
        _.set(_customer, name, val);
        setCustomer(_customer);
    }

    const onInputValueChange = (name, value) => {
        let _customer = { ...customer };
        _.set(_customer, name, value);
        setCustomer(_customer);
    }

    const cleanRegistrationNumber = (value) => {
        return value.replace(/\D/g,'');
    }


    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <Button label={languageService.getText("New")} icon="pi pi-plus" className="p-button-success p-mr-2" onClick={openNew} />
            </React.Fragment>
        )
    }

    const actionBodyTemplate = (rowData) => {
        return (
            <React.Fragment>
                <Button icon="pi pi-pencil" className="p-button-rounded p-button-success p-mr-2" onClick={() => editCustomer(rowData)} />
                <Button icon="pi pi-trash" className="p-button-rounded p-button-warning" onClick={() => confirmDeleteCustomer(rowData)} />
            </React.Fragment>
        );
    }

    const header = (
        <div className="table-header">
            <h5 className="p-m-0">{languageService.getText('Manage_Customers')}</h5>
            <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(e) => {setGlobalFilter(e.target.value); if(e.target.value === "") doSearch(true);}} placeholder={`${languageService.getText('Search')}...`}  onKeyDown={(e) => { if (e.key === 'Enter') { doSearch()}}}/>
            </span>
        </div>
    );

    const customerDialogFooter = (
        <React.Fragment>
            { !saving && <Button label={languageService.getText("Cancel")} icon="pi pi-times" className="p-button-text" onClick={hideDialog} />}
            { !saving && <Button disabled={saveDisabled()} label={languageService.getText("Save")} icon="pi pi-check" className="p-button-text" onClick={saveCustomer} />}
            { saving && <ProgressSpinner style={{width: '20px', height: '20px'}} strokeWidth="8"/>}
        </React.Fragment>
    );
    const deleteCustomerDialogFooter = (
        <React.Fragment>
            { !saving && <Button label={languageService.getText("No")} icon="pi pi-times" className="p-button-text" onClick={hideDeleteCustomerDialog} />}
            { !saving && <Button label={languageService.getText("Yes")} icon="pi pi-check" className="p-button-text" onClick={deleteCustomer} />}
            { saving && <ProgressSpinner style={{width: '20px', height: '20px'}} strokeWidth="8"/>}
        </React.Fragment>
    );

    const formatCNPJ = (value) => {
        return value.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
    }

    const formatCPF = (value) => {
        return value.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }

    if(hasAccess){
        return (
            <div className="datatable-crud-demo">
                <Toast ref={toast} />
                <div className="card">
                    { loading && <ProgressBar mode="indeterminate"/> }
                    { !loading && <Toolbar className="p-mb-4" left={leftToolbarTemplate}></Toolbar>}

                    { !loading &&
                    <>
                    <DataTable ref={dt} value={customers} selection={selectedCustomer} onSelectionChange={(e) => setSelectedCustomer(e.value)}
                        dataKey="id"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageCustomer RowsPerPageDropdown"
                        currentPageCustomerTemplate="Showing {first} to {last} of {totalRecords} customers"
                        header={header}
                        autoLayout={true}
                        onSort={onSort}
                        sortField={sortField} 
                        sortOrder={sortOrder}
                        >

                        <Column field="name" header={languageService.getText("Name")} sortable></Column>
                        <Column field="type" header={languageService.getText("Type")} body={data => data.type === 'PJ' ? languageService.getText("Company") : languageService.getText("Individual")}></Column>
                        <Column field="registration_number" header={languageService.getText("Registration_Number")} body={data => data.type === 'PJ' ? formatCNPJ(data.registration_number) : formatCPF(data.registration_number)}></Column>
                        <Column body={actionBodyTemplate}></Column>
                    </DataTable>
                    <Paginator first={first} rows={rowsPerPage} totalRecords={totalRecords} rowsPerPageOptions={[5, 10, 25, 50, 100]} onPageChange={onPageChange}></Paginator>
                    </>
                    }
                </div>

                <Dialog visible={customerDialog} style={{ width: '90vw' }} header={languageService.getText("Customer_Details")} modal className="p-fluid" footer={customerDialogFooter} onHide={hideDialog}>
                    <div className="p-field">
                        <label htmlFor="name">{languageService.getText("Name")}*</label>
                        <InputText disabled={saving} id="name" value={customer.name} onChange={(e) => onInputChange(e, 'name')} required autoFocus className={classNames({ 'p-invalid': submitted && !customer.name })} />
                        {submitted && !customer.name && <small className="p-invalid">{languageService.getText("Name")} {languageService.getText("is_required")}.</small>}
                    </div>
                    <div className="p-field">
                        <label htmlFor="type">{languageService.getText("Type")}*</label>
                        <Dropdown disabled={saving} id="type" value={customer.type} options={[{label: languageService.getText("Company"), value: 'PJ'},{label: languageService.getText("Individual"), value: 'PF'}]} onChange={(e) => onInputChange(e, 'type')}/>
                        {submitted && !customer.type && <small className="p-invalid">{languageService.getText("Type")} {languageService.getText("is_required")}.</small>}
                    </div>
                    <div className="p-field">
                        <label htmlFor="registration_number">{languageService.getText("Registration_Number")}*</label>
                        <InputMask disabled={saving} autoClear={false} mask={ customer.type === "PJ" ? "99.999.999-9999/99" : "999.999.999-99" } value={customer.registration_number || ""} placeholder={ customer.type === "PJ" ? "__.___.___-____/__" : "___.___.___-__" } unmask={true} onChange={(e) => onInputValueChange('registration_number', cleanRegistrationNumber(e.value))}></InputMask>
                    </div>
                    <Fieldset legend={languageService.getText("Address")}>
                        <div className="p-grid">
                            <div className="p-field p-col-4">
                                <label htmlFor="street">{languageService.getText("Street")}</label>
                                <InputText disabled={saving} id="street" value={customer.address.street || ""} onChange={(e) => onInputChange(e, 'address.street')}/>
                            </div>
                            <div className="p-field p-col-4">
                                <label htmlFor="number">{languageService.getText("Number")}</label>
                                <InputText disabled={saving} id="number" value={customer.address.number || ""} onChange={(e) => onInputChange(e, 'address.number')}/>
                            </div>
                            <div className="p-field p-col-4">
                                <label htmlFor="complement">{languageService.getText("Complement")}</label>
                                <InputText disabled={saving} id="complement" value={customer.address.complement || ""} onChange={(e) => onInputChange(e, 'address.complement')}/>
                            </div>
                            <div className="p-field p-col-4">
                                <label htmlFor="city">{languageService.getText("City")}</label>
                                <InputText disabled={saving} id="city" value={customer.address.city || ""} onChange={(e) => onInputChange(e, 'address.city')}/>
                            </div>
                            <div className="p-field p-col-4">
                                <label htmlFor="state">{languageService.getText("State")}</label>
                                <InputText disabled={saving} id="state" value={customer.address.state || ""} onChange={(e) => onInputChange(e, 'address.state')}/>
                            </div>
                            <div className="p-field p-col-4">
                                <label htmlFor="zipcode">{languageService.getText("ZipCode")}</label>
                                <InputText disabled={saving} id="zipcode" value={customer.address.zipcode || ""} onChange={(e) => onInputChange(e, 'address.zipcode')}/>
                            </div>
                        </div>
                    </Fieldset>
                    <div style={{height: '100px'}}></div>

                </Dialog>

                <Dialog visible={deleteCustomerDialog} style={{ width: '450px' }} header={languageService.getText("Confirm")} modal footer={deleteCustomerDialogFooter} onHide={hideDeleteCustomerDialog}>
                    <div className="confirmation-content">
                        <i className="pi pi-exclamation-triangle p-mr-3" style={{ fontSize: '2rem' }} />
                        {customer && <span>{languageService.getText("Are_you_sure_you_want_to_delete")} <b>{customer.name}</b>?</span>}
                    </div>
                </Dialog>
            </div>
        );
    }
    else {
        return (
            <div className="datatable-crud-demo">
                <div className="card">
                    <b>{languageService.getText("Permission_Denied")}</b>
                </div>
            </div>
        );
    }
}

export default CustomerCrud;